import { AppRoutes } from "@/shared/lib/routes"
import { WebappRoutes } from "@/shared/lib/webapp-routes"
import { createRoute } from "@tanstack/react-router"
import { useEffect } from "react"
import { rootRoute } from "./__root"
import { useNavigateWithParams } from "@/shared/hooks/use-navigate-with-params"
import { ANALYTICS_EVENTS, trackEvent } from "@/features/analytics"
import { MainSignIn, SignInLoader, useSignIn, signOut } from "@/features/auth"
import { NoSubscriptionModal, useSubscriptionVerification } from "@/features/subscriptions"
import { useQuizStore } from "@/stores/quiz-store"
import { usePurchaseStore } from "@/stores/purchase-store"
import { useSubscriptionStore } from "@/stores/subscription-store"

export const signinRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: AppRoutes.SIGN_IN,
  component: SignInPage,
})

/**
 * Main sign-in screen for returning users (landing "Sign in", and the
 * webapp auth-gate redirect land here). After sign-in we verify the
 * subscription: an account with access goes straight to the app; an account
 * without one gets the "no subscription" sheet (take the quiz / switch account)
 * before ever reaching the webapp. The post-purchase account step lives at
 * `/create-account`.
 */
function SignInPage() {
  const navigate = useNavigateWithParams()
  const { status, user, error, handleApple, handleGoogle, dismissError } = useSignIn()
  const { isVerifying, hasAccess } = useSubscriptionVerification()

  const resetQuiz = useQuizStore((s) => s.reset)
  const resetPurchase = usePurchaseStore((s) => s.reset)
  const resetSubscription = useSubscriptionStore((s) => s.reset)

  useEffect(() => {
    trackEvent(ANALYTICS_EVENTS.SIGN_IN_VIEWED)
  }, [])

  useEffect(() => {
    if (status !== "authenticated" || !user) return
    trackEvent(ANALYTICS_EVENTS.SIGN_UP, { userId: user.uid })
  }, [status, user])

  // Only enter the webapp once we've confirmed the account has access.
  useEffect(() => {
    if (status === "authenticated" && user && !isVerifying && hasAccess) {
      navigate({ to: WebappRoutes.ADVISORS })
    }
  }, [status, user, isVerifying, hasAccess, navigate])

  // Take the quiz: wipe the funnel state so they restart cleanly, but keep the
  // Firebase session — a fresh purchase re-binds to the same account at finalize.
  const handleTakeQuiz = () => {
    resetQuiz()
    resetPurchase()
    resetSubscription()
    navigate({ to: AppRoutes.QUIZ })
  }

  // Try another account: sign out and clear the (no-access) subscription state
  // so the sign-in buttons reappear for a different login.
  const handleTryAnother = async () => {
    resetSubscription()
    await signOut()
  }

  // While the session resolves, while verifying, or just before redirecting an
  // account that has access — show a loader instead of flashing the buttons.
  if (status === "loading" || (status === "authenticated" && (isVerifying || hasAccess))) {
    return <SignInLoader />
  }

  const noAccess = status === "authenticated" && !!user && !isVerifying && !hasAccess

  return (
    <>
      <MainSignIn
        onApple={handleApple}
        onGoogle={handleGoogle}
        onQuiz={() => navigate({ to: AppRoutes.QUIZ })}
        error={error}
        onDismissError={dismissError}
      />
      <NoSubscriptionModal
        open={noAccess}
        onTakeQuiz={handleTakeQuiz}
        onTryAnother={handleTryAnother}
      />
    </>
  )
}
